import { Controller } from '@hotwired/stimulus';
import Logger from '../lib/logger';
import { Tour } from '../lib/topology/tour';
import { Inspector } from '../lib/topology/inspector';
import { PanZoom } from '../lib/topology/pan_zoom';
import { PortaledOverlays } from '../lib/topology/overlays';
class default_1 extends Controller {
    constructor() {
        super(...arguments);
        this.zoomLevels = {};
        this.currentModalScale = 1.0;
        this.renderedTabs = {};
        this.mermaid = null;
        // Bumped on disconnect() and captured at the START of each async render (mermaid.run in
        // renderMermaidInTab, mermaid.render in setTopologyMode) so a render already in flight when the
        // controller is disconnected — or superseded by a newer render request before its own await
        // resolves — discards its result instead of mutating DOM state that is stale or gone. Turbo's
        // page-cache restoration reconnects a fresh controller instance onto a snapshot of already-mutated
        // DOM (mermaid's rendered SVG still in place, not the raw source), which is the case this and
        // renderMermaidInTab's own "does this pre already hold an <svg>" check both exist for — instance
        // state like renderedTabs starts fresh on that reconnect and would not catch it on its own.
        this.renderGeneration = 0;
        // Deterministic rather than Date.now(): two mode switches inside the same millisecond used to
        // collide on the id mermaid.render is keyed by.
        this.simRenderCounter = 0;
        this.intersectionObserver = null;
        // Plain DOM references for every target that lives inside the three portaled overlays (the tour
        // card, the inspector drawer, the fullscreen modal, and everything nested inside them). Stimulus's
        // `xxxTarget`/`hasXxxTarget` getters only search within this.element's own subtree — once
        // overlays.ts moves these elements to <body>, those getters can no longer find them and
        // throw/return false. These are captured once, up front, while the elements are still in scope,
        // and handed to Tour/Inspector instead of the Stimulus target getters for these ten targets.
        this.modalBodyEl = null;
        this.modalBackdropEl = null;
        this.modalTitleEl = null;
        this.modalCloseEl = null;
        // The element that opened the fullscreen modal (the ⛶ button), so closeModal() can return focus
        // to exactly where the keyboard started instead of dropping it at the top of the page.
        this.modalOpener = null;
        // The title text as the view wrote it — the fallback for a diagram that carries no
        // data-fullscreen-name of its own (see activateDialog).
        this.modalTitleDefault = '';
        this.inspectorTitleEl = null;
        this.inspectorContentEl = null;
        this.inspectorDrawerEl = null;
        this.tourPopupEl = null;
        this.tourTitleEl = null;
        this.tourBodyEl = null;
        this.tourCounterEl = null;
        this.tourPrevEl = null;
        this.tourNextEl = null;
        // One per diagram, keyed the way the toolbar buttons address them. Created when a diagram is
        // rendered rather than when it is first zoomed, because dragging and ctrl+wheel have to work
        // without anyone pressing a button first.
        this.panZooms = {};
        this.modalPanZoom = null;
        // Registered on document in connect(), removed in disconnect() — see the rationale there.
        // Only the fullscreen modal listens for Escape; nothing else on these pages does.
        this.handleEscape = (event) => {
            if (event.key !== 'Escape')
                return;
            if (!this.modalBackdropEl?.classList.contains('active'))
                return;
            this.closeModal();
        };
    }
    async connect() {
        Logger.debug('TopologyController connected', { controller: this.constructor.name });
        this.captureOverlayElementRefs();
        // Escape closes the fullscreen modal while it is open. A document-level listener, because
        // focus lives on the modal's own Close button and the backdrop is portaled out of
        // this.element's subtree, so no data-action inside it could own the key. Only the modal
        // responds (see handleEscape) — the tour card and the inspector drawer keep their own close
        // affordances, and Bootstrap modals on the page (the in-page VLAN dialog) handle their own.
        document.addEventListener('keydown', this.handleEscape);
        // First paint: until mermaid has rendered them, the <pre>s hold the raw diagram text — a
        // visible flash of code before any diagram (UX review F8, 2026-09-19). diagram-pending hides
        // the source and names the state (see _topology_legacy.scss); a <pre> that already holds a
        // rendered SVG is done — Turbo's page-cache restoration reconnects a fresh controller onto a
        // snapshot the previous instance already rendered — and gets its aria-hidden directly (F3).
        this.element.querySelectorAll('pre.mermaid').forEach(pre => {
            if (pre.querySelector('svg')) {
                pre.setAttribute('aria-hidden', 'true');
            }
            else {
                pre.classList.add('diagram-pending');
            }
        });
        this.tour = new Tour({ getTabContentTargets: () => this.tabContentTargets }, {
            popup: this.tourPopupEl, title: this.tourTitleEl, body: this.tourBodyEl,
            counter: this.tourCounterEl, prev: this.tourPrevEl, next: this.tourNextEl
        }, () => (this.hasTrafficPathValue && this.trafficPathValue) || {}, () => (this.hasFrameBudgetValue && this.frameBudgetValue) || {});
        this.inspector = new Inspector({ title: this.inspectorTitleEl, content: this.inspectorContentEl, drawer: this.inspectorDrawerEl }, () => (this.hasInspectorDataValue ? this.inspectorDataValue : null));
        // Cast: PortaledOverlaysHost's index signature exists so overlays.ts can dispatch a rebound
        // listener to any of this controller's own action methods (startTour, endTour, tourStep,
        // closeInspector, closeModal, zoomModal, resetModalZoom, ...) by name — the same set
        // data-action="...->topology#x" bindings already resolve on this instance directly.
        this.overlays = new PortaledOverlays(this, this.element.className);
        this.overlays.portalToBody([this.tourPopupEl, this.inspectorDrawerEl, this.modalBackdropEl]);
        this.tour.ensureGlowFilters();
        try {
            const mermaidModule = await import('mermaid');
            this.mermaid = mermaidModule.default;
            this.mermaid.initialize({
                startOnLoad: false,
                theme: 'dark',
                themeVariables: {
                    fontSize: '22px',
                    fontFamily: 'Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                    primaryColor: '#1e3a5f',
                    primaryTextColor: '#e2e8f0',
                    primaryBorderColor: '#38bdf8',
                    lineColor: '#64748b',
                    secondaryColor: '#1e293b',
                    tertiaryColor: '#0f172a',
                    mainBkg: '#151c2c',
                    nodeBorder: '#38bdf8',
                    clusterBkg: '#0b0f19',
                    clusterBorder: '#334155',
                    titleColor: '#f8fafc',
                    edgeLabelBackground: '#0f172a'
                },
                flowchart: {
                    padding: 25,
                    nodeSpacing: 50,
                    rankSpacing: 65,
                    htmlLabels: true,
                    curve: 'basis',
                    useMaxWidth: true
                },
                securityLevel: 'strict'
            });
            // Use IntersectionObserver to delay render until the container is visible.
            // This is crucial because if the topology is inside a hidden modal,
            // dagre-d3 will calculate 0x0 bounding boxes and crash with "Could not find a suitable point".
            //
            // Stored on the instance and disconnected in disconnect() too: disconnecting only from inside
            // the intersect callback above leaves the observer running forever for an element that never
            // becomes visible (a topology tab that is never opened), since nothing else ever calls it.
            this.intersectionObserver = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        this.renderMermaidInTab('tab-overview');
                        // Once rendered, we can stop observing
                        this.intersectionObserver?.disconnect();
                    }
                });
            }, { threshold: 0.1 });
            this.intersectionObserver.observe(this.element);
        }
        catch (error) {
            Logger.error('Failed to load Mermaid module dynamically', error);
            // The module never loaded, so none of the diagrams will ever render: the "Drawing…" state
            // must not stay forever unexplained. The failure is named where the diagram should be (F8).
            this.markDiagramsFailed();
        }
    }
    disconnect() {
        document.removeEventListener('keydown', this.handleEscape);
        Object.values(this.panZooms).forEach(panZoom => panZoom.destroy());
        this.panZooms = {};
        this.modalPanZoom?.destroy();
        this.modalPanZoom = null;
        Logger.debug('TopologyController disconnected', { controller: this.constructor.name });
        // Invalidates any mermaid.run/mermaid.render already in flight — see renderGeneration's own
        // comment. Their own .then()/await continuations check this after disconnect can already have
        // run, so bumping it here (rather than only relying on the checks themselves) is what makes a
        // render started a moment before disconnect actually get discarded instead of applied late.
        this.renderGeneration++;
        this.intersectionObserver?.disconnect();
        this.intersectionObserver = null;
        this.tour?.destroy();
        this.overlays?.destroy();
    }
    showTab(event) {
        const btn = event.currentTarget;
        const id = btn.dataset.topologyTabParam;
        if (!id)
            return;
        this.tabContentTargets.forEach(el => el.classList.remove('active'));
        this.element.querySelectorAll('.tab-bar button').forEach(el => el.classList.remove('active'));
        const activeContent = this.tabContentTargets.find(el => el.dataset.tabKey === id);
        if (activeContent)
            activeContent.classList.add('active');
        this.element.querySelectorAll('.tab-bar button').forEach(b => {
            if (b.dataset.topologyTabParam === id) {
                b.classList.add('active');
            }
        });
        requestAnimationFrame(() => {
            this.renderMermaidInTab(`tab-${id}`);
        });
    }
    // The diagram source as written, not as the DOM reports it.
    //
    // Node labels contain literal <br/> markup, which the browser parses into real <br> ELEMENTS
    // inside the <pre>. textContent then drops them silently, so a diagram re-rendered from a captured
    // source lost every line break — each node collapsed into one run-on string
    // ("CCR1009-8G-1S-1S+Identity: INTELLE - 404223 …ROS 7.21.5"). The initial mermaid.run() render
    // was unaffected, so this only showed up after a simulator mode switch.
    //
    // innerHTML keeps the <br/>, but re-escapes the text nodes — "&lt;uni&gt;" comes back as
    // "&amp;lt;uni&amp;gt;", which mermaid would then draw literally. Round-tripping through a
    // textarea undoes exactly one level of escaping while leaving the markup alone, which is the
    // combination the source needs.
    readDiagramSource(pre) {
        const decoder = document.createElement('textarea');
        decoder.innerHTML = pre.innerHTML;
        return decoder.value;
    }
    renderMermaidInTab(tabId) {
        if (this.renderedTabs[tabId])
            return;
        const id = tabId.replace('tab-', '');
        const tab = this.tabContentTargets.find(el => el.dataset.tabKey === id);
        if (!tab)
            return;
        const allPres = Array.from(tab.querySelectorAll('pre.mermaid'));
        // mermaid.run() replaces each <pre>'s content with the rendered SVG, discarding the source.
        // Stash it first: the simulator re-renders the overview diagram from it on every mode switch.
        // Skipped for a <pre> that already holds rendered SVG (see below) — there is no diagram-text
        // source left in it to read.
        allPres.forEach(pre => {
            if (pre.dataset.mermaidSource === undefined && !pre.querySelector('svg')) {
                pre.dataset.mermaidSource = this.readDiagramSource(pre);
            }
        });
        // A <pre> already holding rendered SVG has nothing for mermaid.run to do, and handing it markup
        // mermaid did not write itself is what this guards against — not the double-controller-instance
        // bug renderedTabs above already covers (that one is instance state, reset to empty on every
        // reconnect), but Turbo's page-cache restoration: a fresh controller instance connecting onto a
        // DOM snapshot the PREVIOUS instance already rendered. Only reading the DOM itself catches that.
        const diagrams = allPres.filter(pre => !pre.querySelector('svg'));
        allPres.forEach(pre => {
            const key = pre.dataset.diagramKey;
            if (key)
                this.panZoomFor(key);
        });
        if (diagrams.length > 0 && this.mermaid)
            this.renderNodes(diagrams, tabId);
        this.renderedTabs[tabId] = true;
    }
    // Draws these <pre>s and gives each its settled state: the SVG sized to its card and hidden from
    // assistive tech on success, the failure named on error. Shared by the tab render and by a diagram
    // a Turbo Frame swaps in later (diagramTargetConnected).
    renderNodes(diagrams, context) {
        if (!this.mermaid)
            return;
        const generation = this.renderGeneration;
        this.mermaid.run({ nodes: diagrams }).then(() => {
            // Disconnected while this was in flight — the SVG this would style may already be gone.
            if (generation !== this.renderGeneration)
                return;
            diagrams.forEach(el => {
                const svg = el.querySelector('svg');
                if (svg) {
                    svg.style.maxWidth = '100%';
                    svg.style.width = '100%';
                    svg.style.height = 'auto';
                }
                // Rendered: the raw source is gone, so the "Drawing…" state goes with it. And the SVG is
                // now decorative to assistive tech — the text-form cable/crossing list under the diagram
                // is the representation a screen reader follows, and reading the whole SVG and then the
                // same facts from the table would double them (UX review F3).
                el.classList.remove('diagram-pending');
                el.setAttribute('aria-hidden', 'true');
            });
        }).catch((e) => {
            Logger.error(`Mermaid render error in ${context}`, e);
            // mermaid.run rejects when any node fails; the ones that did render still hold an SVG and
            // keep the success treatment, the rest show their failure state instead of staying as
            // unexplained raw text (UX review F8).
            diagrams.forEach(el => {
                el.classList.remove('diagram-pending');
                if (el.querySelector('svg')) {
                    el.setAttribute('aria-hidden', 'true');
                }
                else {
                    el.classList.add('diagram-failed');
                }
            });
        });
    }
    // A diagram that arrives after the first render — a Turbo Frame swapping one card in place, as the
    // POP page's "Hide N with no configuration" link does — is drawn on its own, with a fresh pan/zoom
    // bound to its new wrapper. The diagrams present at connect pass through here too; the first
    // render draws those, so nothing happens until it has run.
    diagramTargetConnected(pre) {
        if (!this.mermaid || !this.renderedTabs['tab-overview'])
            return;
        if (pre.querySelector('svg'))
            return;
        if (pre.dataset.mermaidSource === undefined)
            pre.dataset.mermaidSource = this.readDiagramSource(pre);
        pre.classList.add('diagram-pending');
        const key = pre.dataset.diagramKey;
        if (key) {
            this.zoomLevels[key] = 1.0;
            this.panZoomFor(key);
        }
        this.renderNodes([pre], `diagram ${key ?? '(unnamed)'}`);
    }
    // The swapped-out diagram's pan/zoom listens on a wrapper that is leaving the page; left in place
    // it would keep its listeners, and panZoomFor() would hand it back for the new diagram.
    diagramTargetDisconnected(pre) {
        const key = pre.dataset.diagramKey;
        if (!key || !this.panZooms[key])
            return;
        this.panZooms[key].destroy();
        delete this.panZooms[key];
    }
    zoomDiagram(event) {
        const btn = event.currentTarget;
        const diagramKey = btn.dataset.topologyDiagramParam;
        const factorStr = btn.dataset.topologyFactorParam;
        if (!diagramKey || !factorStr)
            return;
        const panZoom = this.panZoomFor(diagramKey);
        if (!panZoom)
            return;
        panZoom.zoomBy(parseFloat(factorStr));
        this.zoomLevels[diagramKey] = panZoom.state.scale;
    }
    resetZoom(event) {
        const btn = event.currentTarget;
        const diagramKey = btn.dataset.topologyDiagramParam;
        if (!diagramKey)
            return;
        const panZoom = this.panZoomFor(diagramKey);
        if (!panZoom)
            return;
        panZoom.reset();
        this.zoomLevels[diagramKey] = 1.0;
    }
    // The diagram's own wrapper is what the pointer gestures belong on: the <pre> is the thing being
    // moved, so listening on it would mean the listener moves out from under the pointer mid-drag.
    panZoomFor(diagramKey) {
        const existing = this.panZooms[diagramKey];
        if (existing)
            return existing;
        const el = this.diagramTargets.find(target => target.dataset.diagramKey === diagramKey);
        const wrapper = el?.closest('.diagram-wrapper');
        if (!el || !wrapper)
            return null;
        this.panZooms[diagramKey] = new PanZoom(wrapper, el);
        return this.panZooms[diagramKey];
    }
    openModal(event) {
        const btn = event.currentTarget;
        const diagramKey = btn.dataset.topologyDiagramParam;
        if (!diagramKey)
            return;
        const origDiagram = this.diagramTargets.find(target => target.dataset.diagramKey === diagramKey);
        if (!origDiagram)
            return;
        if (!this.modalBodyEl)
            return;
        const modalBody = this.modalBodyEl;
        modalBody.innerHTML = '';
        // The legend is a sibling of the diagram in the card, and the fullscreen view is the one place
        // where colour semantics are needed most — so it comes along (UX review F2). Every topology
        // view tags its legend with the diagram's own data-diagram-key, which is what this matches on;
        // .diagram-legend is the POP and VLAN pages' class, .legend the circuit page's.
        const legend = this.element.querySelector(`.diagram-legend[data-diagram-key="${CSS.escape(diagramKey)}"], .legend[data-diagram-key="${CSS.escape(diagramKey)}"]`);
        if (legend)
            modalBody.appendChild(legend.cloneNode(true));
        const clone = origDiagram.cloneNode(true);
        clone.id = 'modal-mermaid-content';
        clone.style.transform = 'scale(1.0)';
        this.currentModalScale = 1.0;
        modalBody.appendChild(clone);
        this.modalPanZoom?.destroy();
        // Wider limits than the inline diagrams: fullscreen is where someone goes to read a dense POP
        // diagram closely, and the point of going there is to zoom further than the card allows.
        this.modalPanZoom = new PanZoom(modalBody, clone, { min: 0.3, max: 4.0 });
        if (this.modalBackdropEl)
            this.modalBackdropEl.classList.add('active');
        document.body.style.overflow = 'hidden';
        this.activateDialog(btn, origDiagram);
    }
    // The dialog half of openModal (UX review F2): the title names the diagram that is open — the
    // static fallback text is the same for every diagram and so identifies none of them — focus
    // moves into the dialog, and the opener is remembered so closeModal() can give the keyboard
    // back to it.
    activateDialog(opener, origDiagram) {
        if (this.modalTitleEl) {
            const name = origDiagram.dataset.fullscreenName;
            this.modalTitleEl.textContent = name
                ? `${name} — Fullscreen View`
                : (this.modalTitleDefault || this.modalTitleEl.textContent);
        }
        this.modalOpener = opener;
        this.modalCloseEl?.focus();
    }
    closeModal() {
        if (this.modalBackdropEl)
            this.modalBackdropEl.classList.remove('active');
        document.body.style.overflow = '';
        // The dialog's focus goes back where it started — the ⛶ button that opened it — so a
        // keyboard user is not dropped at the top of the page.
        if (this.modalOpener && document.contains(this.modalOpener))
            this.modalOpener.focus();
        this.modalOpener = null;
    }
    // Every <pre> that never got a rendered SVG: the "Drawing…" state stops and the failure is
    // named where the diagram should be (UX review F8). A <pre> that does hold an SVG is left
    // alone — it rendered.
    markDiagramsFailed() {
        this.element.querySelectorAll('pre.mermaid').forEach(pre => {
            if (pre.querySelector('svg'))
                return;
            pre.classList.remove('diagram-pending');
            pre.classList.add('diagram-failed');
        });
    }
    zoomModal(event) {
        const btn = event.currentTarget;
        const factorStr = btn.dataset.topologyFactorParam;
        if (!factorStr || !this.modalPanZoom)
            return;
        this.modalPanZoom.zoomBy(parseFloat(factorStr));
        this.currentModalScale = this.modalPanZoom.state.scale;
    }
    resetModalZoom() {
        if (!this.modalPanZoom)
            return;
        this.modalPanZoom.reset();
        this.currentModalScale = 1.0;
    }
    // This component has several `position: fixed` overlays (the tour card, the node inspector
    // drawer, the fullscreen modal) that need special positioning handling — see overlays.ts's own
    // header comment for the full rationale. Plain DOM references for every target inside them are
    // captured once, up front, while the elements are still in this.element's own subtree — once
    // overlays.ts moves them to <body>, Stimulus's `xxxTarget` getters can no longer find them.
    captureOverlayElementRefs() {
        this.modalBodyEl = this.hasModalBodyTarget ? this.modalBodyTarget : null;
        this.modalBackdropEl = this.hasModalBackdropTarget ? this.modalBackdropTarget : null;
        this.modalTitleEl = this.hasModalTitleTarget ? this.modalTitleTarget : null;
        this.modalCloseEl = this.hasModalCloseTarget ? this.modalCloseTarget : null;
        // The title as the view wrote it, so a diagram without a data-fullscreen-name keeps its own
        // static heading instead of the previous diagram's.
        this.modalTitleDefault = this.modalTitleEl?.textContent ?? '';
        this.inspectorTitleEl = this.hasInspectorTitleTarget ? this.inspectorTitleTarget : null;
        this.inspectorContentEl = this.hasInspectorContentTarget ? this.inspectorContentTarget : null;
        this.inspectorDrawerEl = this.hasInspectorDrawerTarget ? this.inspectorDrawerTarget : null;
        this.tourPopupEl = this.hasTourPopupTarget ? this.tourPopupTarget : null;
        this.tourTitleEl = this.hasTourTitleTarget ? this.tourTitleTarget : null;
        this.tourBodyEl = this.hasTourBodyTarget ? this.tourBodyTarget : null;
        this.tourCounterEl = this.hasTourCounterTarget ? this.tourCounterTarget : null;
        this.tourPrevEl = this.hasTourPrevTarget ? this.tourPrevTarget : null;
        this.tourNextEl = this.hasTourNextTarget ? this.tourNextTarget : null;
    }
    openNodeInspector(event) {
        this.inspector.open(event);
    }
    openNodeInspectorByKey(key) {
        this.inspector.openByKey(key);
    }
    closeInspector() {
        this.inspector.close();
    }
    // data-topology-tour-param picks which tour; absent means the path tour, so an older trigger that
    // does not pass one keeps working.
    startTour(event) {
        const requested = event?.currentTarget?.dataset.topologyTourParam;
        this.tour.start(requested === 'encap' ? 'encap' : 'path');
    }
    endTour() {
        this.tour.end();
    }
    tourStep(event) {
        const btn = event.currentTarget;
        const stepParam = btn.dataset.topologyStepParam;
        if (!stepParam)
            return;
        this.tour.step(parseInt(stepParam, 10));
    }
    filterTables(event) {
        const input = event.currentTarget;
        const filter = input.value.toLowerCase();
        const activeTab = this.tabContentTargets.find(el => el.classList.contains('active'));
        if (!activeTab)
            return;
        const tables = activeTab.querySelectorAll('.filterable-table');
        tables.forEach(table => {
            const rows = table.querySelectorAll('tbody tr');
            rows.forEach(row => {
                const text = row.textContent?.toLowerCase() || '';
                if (text.includes(filter)) {
                    row.style.display = '';
                }
                else {
                    row.style.display = 'none';
                }
            });
        });
    }
    // Simulator for the HA dual-CPE architecture: redraws the overview diagram as it would look under
    // a primary-WAN outage or an ISP outage.
    //
    // The three states are the same graph with a different set of linkStyle rules — which paths are
    // animated, which are muted, which are hidden — so only that block is carried per mode (in
    // data-topology-modes) and swapped onto the stored source. Rendering through mermaid.render rather
    // than mermaid.run because the <pre> now holds an SVG, not diagram text.
    async setTopologyMode(event) {
        const btn = event.currentTarget;
        const mode = btn.dataset.topologyModeParam || 'normal';
        const group = btn.closest('.mode-switch-group');
        const pre = this.element.querySelector('#diagram-tab-overview');
        if (!group || !pre || !this.mermaid)
            return;
        // The definitions live in a JSON script tag rather than on the button group because they are
        // built from the edge indices recorded WHILE the diagram is emitted — which the template only
        // knows after the diagram, and the toolbar is rendered before it.
        const defs = this.element.querySelector('[data-topology-mode-definitions="tab-overview"]');
        let modes;
        try {
            modes = JSON.parse(defs?.textContent || group.dataset.topologyModes || '{}');
        }
        catch (e) {
            Logger.error('setTopologyMode: could not parse mode definitions', e);
            return;
        }
        const spec = modes[mode];
        if (!spec)
            return;
        // The stored source still carries the normal-mode linkStyle block; drop it before appending the
        // selected one, or mermaid applies whichever rule comes last per link and the modes bleed.
        const source = (pre.dataset.mermaidSource || '')
            .split('\n')
            .filter(line => !line.trim().startsWith('linkStyle'))
            .join('\n')
            .trimEnd();
        // Deterministic rather than Date.now(): two mode switches inside the same millisecond used to
        // collide on the id mermaid.render is keyed by.
        const generation = this.renderGeneration;
        try {
            const { svg, bindFunctions } = await this.mermaid.render(`sim-${mode}-${++this.simRenderCounter}`, `${source}\n${spec.link_styles.join('\n')}`);
            // Disconnected, or superseded by a newer mode switch, while this await was in flight — the
            // <pre> this would write into may already be gone, or a later render already owns it.
            if (generation !== this.renderGeneration)
                return;
            pre.innerHTML = svg;
            const svgEl = pre.querySelector('svg');
            if (svgEl) {
                svgEl.style.maxWidth = '100%';
                svgEl.style.width = '100%';
                svgEl.style.height = 'auto';
                if (bindFunctions)
                    bindFunctions(svgEl);
            }
        }
        catch (e) {
            Logger.error(`setTopologyMode: mermaid failed to render mode '${mode}'`, e);
            return;
        }
        group.querySelectorAll('.btn-mode').forEach(el => {
            const b = el;
            const isActive = b.dataset.topologyModeParam === mode;
            b.className = isActive ? `btn-mode active-${spec.label}` : 'btn-mode';
        });
        this.simulationBannerTargets.forEach(banner => {
            banner.className = spec.banner_class;
            const span = banner.querySelector('span');
            if (span)
                span.innerHTML = spec.banner;
        });
    }
    handleMermaidClick(event) {
        this.inspector.handleMermaidClick(event);
    }
}
default_1.targets = [
    'diagram',
    'tabContent',
    'modalBody',
    'modalBackdrop',
    'modalTitle',
    'modalClose',
    'inspectorTitle',
    'inspectorContent',
    'inspectorDrawer',
    'tourPopup',
    'tourTitle',
    'tourBody',
    'tourCounter',
    'tourPrev',
    'tourNext',
    'simulationBanner'
];
default_1.values = {
    inspectorData: Object,
    trafficPath: Object,
    frameBudget: Object
};
export default default_1;
